// Per-page public SEO/AEO/GEO metadata + a factual structured-data entity graph.
// No reviews, ratings, awards, endorsements, user counts or performance/profit claims.
// Authoritative public domain: https://tradecraftonline.com

const ORIGIN =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.PUBLIC_SITE_URL ||
  process.env.REACT_APP_PUBLIC_SITE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "https://tradecraftonline.com";

const abs = (p) => `${ORIGIN}${p}`;
export const socialImage = (name) => `/social/${name}.jpg`;

const ORG_ID = `${ORIGIN}/#organization`;
const WEBSITE_ID = `${ORIGIN}/#website`;
const SOFTWARE_ID = `${ORIGIN}/#software`;

// ---- Sitewide entities (stable @id, consolidated across every page) ----
const ORG = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "TRADECRAFT",
  url: ORIGIN,
  slogan: "Learn the market. Master the craft.",
  description:
    "AI-native market intelligence and trader-development platform. Educational, simulation-first — not financial advice and no real-money execution. Built by a father-and-son team, Brett Payne and Benjamin Payne.",
  logo: { "@type": "ImageObject", url: abs("/icon-512.png"), width: 512, height: 512 },
  founder: [
    { "@type": "Person", "@id": `${ORIGIN}/#founder-brett-payne`, name: "Brett Payne",
      description: "The father in the father-and-son team that built TRADECRAFT." },
    { "@type": "Person", "@id": `${ORIGIN}/#founder-benjamin-payne`, name: "Benjamin Payne",
      description: "The son in the father-and-son team that built TRADECRAFT." },
  ],
};
const WEBSITE = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "TRADECRAFT",
  url: ORIGIN,
  inLanguage: "en-US",
  publisher: { "@id": ORG_ID },
  description:
    "AI-native market intelligence that surfaces what deserves attention, shows the evidence, and develops your judgment.",
};
const SOFTWARE = {
  "@type": "SoftwareApplication",
  "@id": SOFTWARE_ID,
  name: "TRADECRAFT",
  url: ORIGIN,
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  publisher: { "@id": ORG_ID },
  description:
    "AI-native market intelligence that screens supported crypto markets, separates deterministic market evidence from AI interpretation, and develops trading judgment in a simulation-first, educational environment. Not a signal service, not financial advice, no real-money execution.",
  featureList: [
    "Market Intelligence — screens supported crypto markets and surfaces what deserves attention",
    "Analyst — deterministic evidence plus AI interpretation with primary and alternative thesis",
    "Human + AI workflow — analyze first, then compare",
    "Historical replay and scenarios with no look-ahead",
    "Process Score and competency development",
    "Family model with guardian authorization separate from earned competency",
  ],
  offers: [
    { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
    { "@type": "Offer", name: "Pro", price: "39", priceCurrency: "USD" },
    { "@type": "Offer", name: "Family", price: "59", priceCurrency: "USD" },
  ],
};
const SITEWIDE = [ORG, WEBSITE, SOFTWARE];

// faqJsonLd returns a FAQPage node for inclusion inside a page @graph (no @context here).
export const faqJsonLd = (qa) => ({
  "@type": "FAQPage",
  mainEntity: qa.map(([q, a]) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

// Standard per-page node set (WebPage/CollectionPage + BreadcrumbList) wired to sitewide @ids.
function pageNodes({ path, name, description, image, type = "WebPage", breadcrumb = [] }) {
  const pageId = `${abs(path)}#webpage`;
  const crumbs = [["Home", "/"], ...breadcrumb];
  return [
    {
      "@type": type,
      "@id": pageId,
      url: abs(path),
      name,
      description,
      isPartOf: { "@id": WEBSITE_ID },
      about: { "@id": SOFTWARE_ID },
      inLanguage: "en-US",
      primaryImageOfPage: { "@type": "ImageObject", url: abs(image) },
      breadcrumb: { "@id": `${abs(path)}#breadcrumb` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${abs(path)}#breadcrumb`,
      itemListElement: crumbs.map(([n, p], i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: n,
        item: abs(p),
      })),
    },
  ];
}

const pageGraph = (opts, extra = []) => ({
  "@context": "https://schema.org",
  "@graph": [...SITEWIDE, ...pageNodes(opts), ...extra],
});

// ---- Glossary (first-class educational resource; feeds /glossary + DefinedTermSet) ----
export const GLOSSARY = [
  ["AI-native market intelligence", "An approach where market screening, evidence and interpretation are built around AI from the ground up: a deterministic engine computes observable market facts and AI reasons over them, surfacing what deserves attention instead of issuing signals.", "/intelligence"],
  ["Attention Queue", "A short, ranked list of the market situations currently worth investigating, distilled from the full screened universe so you focus on what matters now.", "/intelligence"],
  ["Attention Priority", "The ranking that orders the Attention Queue by how much a situation warrants a closer look. It reflects structure and meaningful change — never a probability of profit.", "/intelligence"],
  ["Setup maturity", "Where a situation sits in its lifecycle, tracked through states from early formation to trigger or invalidation, so you can see how a setup is developing.", "/intelligence"],
  ["EARLY", "A setup-maturity state indicating a situation is only beginning to form; structure is developing and no trigger condition is near.", "/intelligence"],
  ["DEVELOPING", "A setup-maturity state indicating structure is building and the situation is progressing, but has not yet approached a trigger.", "/intelligence"],
  ["NEAR TRIGGER", "A setup-maturity state indicating a situation is approaching the conditions that would make it actionable.", "/intelligence"],
  ["TRIGGERED", "A setup-maturity state indicating the defined conditions for the situation have been met.", "/intelligence"],
  ["INVALIDATED", "A setup-maturity state indicating the conditions that defined the thesis have been broken, so the original read no longer holds.", "/analyst"],
  ["Deterministic market analysis", "Objective, repeatable calculation of observable market facts — structure, trend, momentum, levels, risk/reward — that produces the same result from the same data, independent of AI.", "/analyst"],
  ["AI interpretation", "The reasoning layer that reads over deterministic evidence to form a thesis. It is clearly labelled, can be wrong, and cannot silently invent or override the calculated facts.", "/analyst"],
  ["Human + AI analysis", "A workflow where you commit your own read first, TRADECRAFT analyzes independently, and you compare — using AI to sharpen judgment rather than replace it.", "/how-it-works"],
  ["Trading thesis", "A structured explanation of what a trader expects and why, grounded in specific evidence and paired with the conditions that would prove it wrong.", "/analyst"],
  ["Alternative thesis", "A credible competing interpretation of the same evidence, shown alongside the primary thesis so a single narrative is never assumed.", "/analyst"],
  ["Thesis invalidation", "The explicit condition that would prove a thesis wrong. Every TRADECRAFT thesis carries one so you know in advance when to abandon it.", "/analyst"],
  ["Confluence", "The degree to which independent pieces of evidence agree. Stronger confluence means more factors point the same way; conflicts are shown rather than hidden.", "/learn"],
  ["Multi-timeframe analysis", "Evaluating a market across several timeframes at once to see whether higher- and lower-timeframe structure align or disagree.", "/learn"],
  ["Process Score", "A measure of decision quality — setup, risk, plan, execution, psychology and reflection — that evaluates how well you traded, independent of whether the outcome won or lost.", "/learn"],
  ["PASS", "A valid, professional decision to take no trade when evidence or conditions don't justify one. In TRADECRAFT, choosing not to trade can be the correct process.", "/learn"],
  ["Historical replay", "Practicing on real past market data that advances candle by candle, so you experience how structure unfolded without knowing what happens next.", "/learn"],
  ["No look-ahead", "A guarantee that historical training never leaks future data into the past, so replay and scenarios test genuine decision-making.", "/learn"],
  ["Competency", "Demonstrated skill across market-reading dimensions, earned through activity and assessment. Competency — not payment — determines access to advanced capabilities.", "/how-it-works"],
  ["Coach", "An assistance mode focused on teaching: guiding you through concepts and building foundational market-reading skill.", "/how-it-works"],
  ["Copilot", "An assistance mode that supports you while you work, offering contextual help as you analyze — without deciding for you.", "/how-it-works"],
  ["Analyst", "A capability that produces structured analysis of a single market: deterministic evidence plus AI interpretation with a thesis, an alternative, conflicting evidence and explicit invalidation.", "/analyst"],
  ["Intelligence", "A capability that screens the whole supported universe and surfaces the few situations that currently deserve attention, with maturity states and priority.", "/intelligence"],
];

const glossaryTermSet = {
  "@type": "DefinedTermSet",
  "@id": `${abs("/glossary")}#glossary`,
  name: "TRADECRAFT Glossary",
  url: abs("/glossary"),
  inLanguage: "en-US",
  hasDefinedTerm: GLOSSARY.map(([term, def]) => ({
    "@type": "DefinedTerm",
    name: term,
    description: def,
    inDefinedTermSet: { "@id": `${abs("/glossary")}#glossary` },
  })),
};

export const SEO = {
  home: {
    title: null,
    description:
      "AI-native market intelligence and simulated trading practice designed to develop your judgment. Transferable trading skills, taught with crypto-market examples.",
    path: "/",
    image: socialImage("home"),
    imageAlt: "TRADECRAFT — AI-native market intelligence",
    jsonLd: pageGraph({
      path: "/",
      name: "TRADECRAFT — AI-native market intelligence & trading education",
      description: "AI-native market intelligence that surfaces what deserves attention, shows the evidence, and develops your judgment.",
      image: socialImage("home"),
    }),
  },
  intelligence: {
    title: "Market Intelligence — Know What Deserves Your Attention | TRADECRAFT",
    description:
      "TRADECRAFT Intelligence screens supported markets and ranks the few situations worth a closer look, with evidence shown. A priority list, not a buy/sell signal.",
    path: "/intelligence",
    image: socialImage("intelligence"),
    imageAlt: "TRADECRAFT Intelligence — screening markets down to a short attention queue",
    jsonLd: pageGraph({
      path: "/intelligence",
      name: "Market Intelligence — TRADECRAFT",
      description: "Screens supported crypto markets and surfaces the few situations that currently deserve attention.",
      image: socialImage("intelligence"),
      breadcrumb: [["Intelligence", "/intelligence"]],
    }),
  },
  analyst: {
    title: "Analyst — AI Chart Analysis That Shows Its Evidence | TRADECRAFT",
    description:
      "AI chart analysis that shows its work: computed evidence kept apart from AI interpretation, with an alternative view and the point where the read is wrong.",
    path: "/analyst",
    image: socialImage("analyst"),
    imageAlt: "TRADECRAFT Analyst — deterministic evidence beside AI interpretation",
    jsonLd: pageGraph({
      path: "/analyst",
      name: "Analyst — TRADECRAFT",
      description: "Deterministic market evidence plus AI interpretation, with a thesis, an alternative, conflicting evidence and explicit invalidation.",
      image: socialImage("analyst"),
      breadcrumb: [["Analyst", "/analyst"]],
    }),
  },
  "how-it-works": {
    title: "How TRADECRAFT Works — You Analyze First, Then Compare With the AI | TRADECRAFT",
    description:
      "You make your own market read first, then compare it with TRADECRAFT's independent AI analysis. A simple loop that builds your judgment instead of replacing it.",
    path: "/how-it-works",
    image: socialImage("how-it-works"),
    imageAlt: "TRADECRAFT — analyze first, then compare with AI",
    jsonLd: pageGraph({
      path: "/how-it-works",
      name: "How It Works — TRADECRAFT",
      description: "Commit your own read first, compare with independent AI analysis, and build judgment as competency grows.",
      image: socialImage("how-it-works"),
      breadcrumb: [["How It Works", "/how-it-works"]],
    }),
  },
  learn: {
    title: "Learn Trading With No Experience — Staged, Practice-First | TRADECRAFT",
    description:
      "New to trading? Start at Stage 1 with zero experience. Learn to read charts, manage risk and practice in simulation without risking real money, stage by stage.",
    path: "/learn",
    image: socialImage("learn"),
    imageAlt: "TRADECRAFT Learn — process score and skill mastery",
    jsonLd: pageGraph({
      path: "/learn",
      name: "Learn — TRADECRAFT",
      description: "Connect learning to real market behavior with replay, scenarios, skill mastery and a process-over-outcome model.",
      image: socialImage("learn"),
      breadcrumb: [["Learn", "/learn"]],
    }),
  },
  family: {
    title: "Teach Your Teenager About Investing — Safely, With No Real Money | TRADECRAFT",
    description:
      "Help your teenager learn investing safely: supervised, guardian-linked accounts, simulation only with no real money, and skills earned step by step. Start free.",
    path: "/family",
    image: socialImage("family"),
    imageAlt: "TRADECRAFT Family — guardian authorization and simulation-first learning",
    jsonLd: pageGraph({
      path: "/family",
      name: "Family — TRADECRAFT",
      description: "A simulation-first environment for developing traders, with guardian authorization kept separate from earned competency.",
      image: socialImage("family"),
      breadcrumb: [["Family", "/family"]],
    }),
  },
  pricing: {
    title: "Pricing — Start Learning Trading Free, No Card Required | TRADECRAFT",
    description:
      "Compare TRADECRAFT plans: Free at $0, Pro at $39 a month and Family at $59 a month. Start learning trading free with no card required. Upgrade only if you want.",
    path: "/pricing",
    image: socialImage("pricing"),
    imageAlt: "TRADECRAFT pricing — Free, Pro and Family plans",
    jsonLd: pageGraph({
      path: "/pricing",
      name: "Pricing — TRADECRAFT",
      description: "Free, Pro and Family plans. Plans expand what's available; they never bypass competency or guardian authorization.",
      image: socialImage("pricing"),
      breadcrumb: [["Pricing", "/pricing"]],
    }),
  },
  glossary: {
    title: "Trading Glossary — Plain-English Definitions | TRADECRAFT",
    description:
      "Plain-English definitions of trading terms like candlestick, support, resistance, stop-loss and risk-reward, plus the TRADECRAFT concepts used in the product.",
    path: "/glossary",
    image: socialImage("default"),
    imageAlt: "TRADECRAFT glossary of market-intelligence and trading terms",
    jsonLd: pageGraph(
      {
        path: "/glossary",
        name: "Glossary — TRADECRAFT",
        description: "Definitions of the concepts behind TRADECRAFT and the market-analysis terminology used throughout the product.",
        image: socialImage("default"),
        type: "CollectionPage",
        breadcrumb: [["Glossary", "/glossary"]],
      },
      [glossaryTermSet],
    ),
  },
  legal: {
    title: "Legal & Trust",
    description:
      "TRADECRAFT Terms & Conditions and Privacy Policy, plus summaries of the educational disclaimer, risk disclosure, AI transparency and data attribution.",
    path: "/legal",
    image: socialImage("default"),
    imageAlt: "TRADECRAFT legal and trust overview",
    jsonLd: pageGraph({
      path: "/legal",
      name: "Legal & Trust — TRADECRAFT",
      description: "Educational disclaimer, terms, privacy, risk disclosure, AI transparency and data attribution.",
      image: socialImage("default"),
      breadcrumb: [["Legal & Trust", "/legal"]],
    }),
  },
  insights: {
    title: "Insights — Checking and Challenging AI Market Reads | TRADECRAFT",
    description:
      "TRADECRAFT Insights is a developing resource on checking AI market reads against computed evidence, with conflict, invalidation and PASS as legitimate answers.",
    path: "/insights",
    image: socialImage("default"),
    imageAlt: "TRADECRAFT Insights — verifying AI market reads against computed evidence",
    jsonLd: pageGraph({
      path: "/insights",
      name: "Insights — TRADECRAFT",
      description: "Methodology and analysis on verifying AI market reads against deterministic, computed market evidence.",
      image: socialImage("default"),
      type: "CollectionPage",
      breadcrumb: [["Insights", "/insights"]],
    }),
  },
};

// Beginner term pages (/glossary/<slug>).
export const termSeo = (t) => ({
  title: `${t.term} — Plain-English Definition | TRADECRAFT`,
  description: t.short,
  path: `/glossary/${t.slug}`,
  image: socialImage("default"),
  imageAlt: `${t.term} explained in plain English`,
  jsonLd: pageGraph(
    { path: `/glossary/${t.slug}`, name: `${t.term} — TRADECRAFT Glossary`, description: t.short,
      image: socialImage("default"), breadcrumb: [["Glossary", "/glossary"], [t.term, `/glossary/${t.slug}`]] },
    [{ "@type": "DefinedTerm", "@id": `${abs(`/glossary/${t.slug}`)}#term`, name: t.term, description: t.short,
       url: abs(`/glossary/${t.slug}`), inDefinedTermSet: { "@id": `${abs("/glossary")}#glossary` } }],
  ),
});
